import cn from 'classnames'
import {FC, ReactNode, useState} from 'react'

import {TextFontWeight} from '@/components/common/content/Text'
import {ChevronIcon} from '@/components/common/icons/ChevronIcon'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {Currency} from '@/utils/currency'
import {analytics} from '@/utils/tracking/analytics'

import {FormSummaryItemWithPrice} from './FormSummaryItemWithPrice'

type FormSummaryItemBreakdownProps = {
  title: string
  price?: number
  description?: ReactNode
  tooltip?: ReactNode
  fontWeight?: TextFontWeight
  textColor?: string
  isLowercase?: boolean
  hasDecimal?: boolean
  dataTest?: string
  trackedEvent?: {name: string; parameters?: any}
  currency?: Currency | undefined
  breakdownItems?: ReactNode
  isExpanded?: boolean
  iconPosition?: 'left' | 'right'
}

export const FormSummaryItemBreakdown: FC<
  React.PropsWithChildren<FormSummaryItemBreakdownProps>
> = ({
  title,
  price,
  description,
  currency,
  tooltip,
  fontWeight,
  textColor,
  isLowercase,
  hasDecimal,
  trackedEvent,
  dataTest,
  breakdownItems,
  isExpanded = false,
  iconPosition = 'left',
  children,
}) => {
  const [isBreakdownVisible, setIsBreakdownVisible] = useState<boolean>(isExpanded)

  return (
    <>
      <div className="wrapper" data-test={dataTest}>
        <button
          className="button"
          onClick={() => {
            setIsBreakdownVisible(!isBreakdownVisible)
            trackedEvent && analytics.track(trackedEvent.name, trackedEvent.parameters)
          }}
          type="button"
        >
          {iconPosition === 'left' && (
            <div className={cn('chevron', isBreakdownVisible && 'active')}>
              <ChevronIcon variant="right" color={textColor} />
            </div>
          )}
          <div className={cn('content', iconPosition === 'right' && 'full-width')}>
            <FormSummaryItemWithPrice
              title={title}
              price={price}
              description={description}
              tooltip={tooltip}
              fontWeight={fontWeight}
              textColor={textColor}
              isLowercase={isLowercase}
              hasDecimal={hasDecimal}
              currency={currency}
              hasDropdownIcon={iconPosition === 'right'}
              isBreakdownVisible={isBreakdownVisible}
            />
          </div>
        </button>
        <div className={cn('breakdown', isBreakdownVisible && 'visible')}>{breakdownItems}</div>
        {children}
      </div>

      <style jsx>{`
        .wrapper + .wrapper {
          margin-top: ${theme.spacing[3]};
        }
        .button {
          display: flex;
          align-items: center;
          text-align: left;
          width: 100%;
          cursor: pointer;
        }
        .chevron {
          margin-right: ${theme.spacing[2]};
          transition: transform ${EXPAND_TRANSITION}ms ease-in-out;
        }
        .active {
          transform: rotate(90deg);
        }
        .content {
          width: calc(100% - 28px);
        }
        .content.full-width {
          width: 100%;
        }
        .breakdown {
          opacity: 0;
          visibility: hidden;
          max-height: 0;
          transition: all ${EXPAND_TRANSITION}ms ease-in-out;
        }
        .visible {
          opacity: 1;
          visibility: visible;
          max-height: 100%;
          margin-top: ${theme.spacing[2]};
        }
      `}</style>
    </>
  )
}
